import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Code, ConnectError } from '@connectrpc/connect';
import { SpecraButton, SpecraCheckboxField } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { projectDataClient } from '../../../lib/connect/clients';
import { Facts, TextField } from '../PastPerformance/Fields';
import { outputsKey } from './hooks';
import { dateLabel } from './outputPresentationModel';
const draftOf = (head)=>({
        narrative: head?.narrative ?? '',
        evidence: head?.evidence ?? '',
        limitations: head?.limitations ?? '',
        reviewed: head?.reviewed ?? false
    });
export function MaterialEditor({ captureProjectId, sectionKey, head, history, onDenied, sessions }) {
    const cache = useQueryClient();
    const session = sessions?.get(sectionKey);
    const [draft, setDraft] = useState(()=>session?.draft ?? draftOf(head));
    const [version, setVersion] = useState(session?.version ?? head?.version ?? 0);
    const [busy, setBusy] = useState(false);
    const inFlight = useRef(false);
    const [error, setError] = useState('');
    const [saved, setSaved] = useState('');
    const [conflict, setConflict] = useState(session?.conflict ?? false);
    const [latest, setLatest] = useState(session?.latest ?? null);
    const [loadedLatest, setLoadedLatest] = useState(session?.loadedLatest ?? false);
    useEffect(()=>{
        sessions?.set(sectionKey, {
            draft,
            version,
            conflict,
            latest,
            loadedLatest
        });
    }, [
        sessions,
        sectionKey,
        draft,
        version,
        conflict,
        latest,
        loadedLatest
    ]);
    async function save() {
        if (inFlight.current || conflict) return;
        inFlight.current = true;
        setBusy(true);
        setError('');
        setSaved('');
        try {
            const result = await projectDataClient.saveCaptureOutputMaterial({
                captureProjectId,
                sectionKey,
                expectedVersion: version,
                ...draft
            });
            if (!result.material) throw new Error('No saved revision was returned. Reload before saving again.');
            setVersion(result.material.version);
            setSaved(`Saved revision ${result.material.version} · ${dateLabel(result.material.createdAt)}`);
            await cache.invalidateQueries({
                queryKey: outputsKey(captureProjectId)
            });
        } catch (cause) {
            const code = ConnectError.from(cause).code;
            if (code === Code.PermissionDenied || code === Code.Unauthenticated || code === Code.NotFound) {
                onDenied();
                return;
            }
            if (code === Code.Aborted) {
                setConflict(true);
                setError('Another revision was saved. Your draft is retained. Load the latest saved material, compare it, then reconcile before saving.');
            } else setError('Material could not be saved. Your draft is retained; retry when the connection is available.');
        } finally{
            inFlight.current = false;
            setBusy(false);
        }
    }
    async function reload() {
        if (inFlight.current) return;
        inFlight.current = true;
        setBusy(true);
        setError('');
        try {
            const result = await projectDataClient.getCaptureOutputs({
                captureProjectId,
                templateId: 'capture-review-v1'
            });
            const material = result.projection?.sections.find((section)=>section.key === sectionKey)?.manualMaterial;
            setLatest(material ?? null);
            setLoadedLatest(true);
        } catch (cause) {
            const code = ConnectError.from(cause).code;
            if (code === Code.PermissionDenied || code === Code.Unauthenticated || code === Code.NotFound) onDenied();
            else setError('Latest material could not be loaded. Your draft is retained. Retry loading.');
        } finally{
            inFlight.current = false;
            setBusy(false);
        }
    }
    return /*#__PURE__*/ _jsxs("section", {
        className: "space-y-3 rounded-md border p-4",
        "data-testid": Testids.captureOutputs.materialEditor,
        children: [
            /*#__PURE__*/ _jsx("h3", {
                className: "font-semibold",
                children: "Outputs manual material"
            }),
            /*#__PURE__*/ _jsx("p", {
                className: "text-sm text-muted-foreground",
                children: "Save partial or empty drafts. Narrative, evidence and internal review are needed for fallback readiness. Manual material supplies coverage only when a structured source is absent; it cannot repair incomplete owning records. Supplementary material remains available here."
            }),
            /*#__PURE__*/ _jsxs("p", {
                className: "text-sm",
                children: [
                    "Editing from saved revision ",
                    version || 'none'
                ]
            }),
            /*#__PURE__*/ _jsxs("fieldset", {
                disabled: busy,
                className: "space-y-3",
                children: [
                    /*#__PURE__*/ _jsx(TextField, {
                        label: "Narrative",
                        multiline: true,
                        testId: Testids.captureOutputs.narrative,
                        value: draft.narrative,
                        onChange: (narrative)=>setDraft({
                                ...draft,
                                narrative
                            })
                    }),
                    /*#__PURE__*/ _jsx(TextField, {
                        label: "Evidence and source references",
                        multiline: true,
                        testId: Testids.captureOutputs.evidence,
                        value: draft.evidence,
                        onChange: (evidence)=>setDraft({
                                ...draft,
                                evidence
                            })
                    }),
                    /*#__PURE__*/ _jsx(TextField, {
                        label: "Limitations and open gaps",
                        multiline: true,
                        testId: Testids.captureOutputs.limitations,
                        value: draft.limitations,
                        onChange: (limitations)=>setDraft({
                                ...draft,
                                limitations
                            })
                    }),
                    /*#__PURE__*/ _jsx(SpecraCheckboxField, {
                        "data-testid": Testids.captureOutputs.reviewed,
                        label: "Internally reviewed for capture use",
                        controlProps: {
                            checked: draft.reviewed,
                            onCheckedChange: (reviewed)=>setDraft({
                                    ...draft,
                                    reviewed: reviewed === true
                                })
                        }
                    })
                ]
            }),
            error && /*#__PURE__*/ _jsx("p", {
                role: "alert",
                children: error
            }),
            conflict && /*#__PURE__*/ _jsxs("div", {
                "data-testid": Testids.captureOutputs.conflict,
                className: "space-y-3 rounded-md border border-border p-3",
                children: [
                    /*#__PURE__*/ _jsx("p", {
                        children: "Your draft above will remain unchanged when loading the latest revision."
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        variant: "secondary",
                        "data-testid": Testids.captureOutputs.reload,
                        disabled: busy,
                        onClick: reload,
                        children: busy ? 'Loading…' : 'Load latest for comparison'
                    }),
                    loadedLatest && /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsxs(Facts, {
                                title: `Latest saved material · revision ${latest?.version ?? 0}`,
                                children: [
                                    /*#__PURE__*/ _jsxs("p", {
                                        children: [
                                            "Narrative: ",
                                            latest?.narrative || 'Empty'
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("p", {
                                        children: [
                                            "Evidence: ",
                                            latest?.evidence || 'Empty'
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("p", {
                                        children: [
                                            "Limitations: ",
                                            latest?.limitations || 'None recorded'
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("p", {
                                        children: [
                                            "Internally reviewed: ",
                                            latest?.reviewed ? 'Yes' : 'No'
                                        ]
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                className: "text-sm",
                                children: "Reconcile the draft above with this saved material. Continuing sets the latest revision as your edit basis; saving remains a separate action."
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                variant: "secondary",
                                "data-testid": Testids.captureOutputs.reconcile,
                                onClick: ()=>{
                                    setVersion(latest?.version ?? 0);
                                    setConflict(false);
                                    setLoadedLatest(false);
                                    setError('');
                                },
                                children: "Use latest revision as basis for my reconciled draft"
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(SpecraButton, {
                "data-testid": Testids.captureOutputs.materialSave,
                disabled: busy || conflict,
                onClick: save,
                children: busy ? 'Saving…' : 'Save manual material'
            }),
            saved && /*#__PURE__*/ _jsx("output", {
                children: saved
            }),
            /*#__PURE__*/ _jsxs("details", {
                "data-testid": Testids.captureOutputs.materialHistory,
                children: [
                    /*#__PURE__*/ _jsxs("summary", {
                        className: "cursor-pointer",
                        children: [
                            "Manual revision history (",
                            history.length,
                            ")"
                        ]
                    }),
                    history.length === 0 && /*#__PURE__*/ _jsx("p", {
                        children: "No saved manual revisions."
                    }),
                    history.map((material)=>/*#__PURE__*/ _jsxs(Facts, {
                            title: `Revision ${material.version} · ${dateLabel(material.createdAt)}`,
                            children: [
                                /*#__PURE__*/ _jsx("p", {
                                    children: material.narrative || 'Empty narrative'
                                }),
                                /*#__PURE__*/ _jsxs("p", {
                                    children: [
                                        "Evidence: ",
                                        material.evidence || 'Not recorded'
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("p", {
                                    children: [
                                        "Limitations: ",
                                        material.limitations || 'None recorded'
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("p", {
                                    children: [
                                        "Internally reviewed: ",
                                        material.reviewed ? 'Yes' : 'No'
                                    ]
                                })
                            ]
                        }, material.id))
                ]
            })
        ]
    });
}
